import { create } from 'zustand';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { Project, Scene, Day, Viewport, Pin, MapAnnotation, AnnotationType } from '../types/index.js';

// Module-level map instance for non-React access (e.g. Make Map bounds)
let _mapInstance: MapboxMap | null = null;
export const setGlobalMapInstance = (m: MapboxMap | null) => { _mapInstance = m; };
export const getGlobalMapInstance = () => _mapInstance;

interface MapState {
  viewport: Viewport;
  activeProject: Project | null;
  activeScene: Scene | null;
  activeDay: Day | null;
  selectedPinId: string | null;
  activeLocationId: string | null;
  detailPin: Pin | null;
  showTraffic: boolean;
  mapMode: boolean;
  annotations: MapAnnotation[];
  activeAnnotationTool: AnnotationType | null;

  setViewport: (v: Viewport) => void;
  flyTo: (lng: number, lat: number, zoom?: number) => void;
  setActiveProject: (p: Project | null) => void;
  setActiveScene: (s: Scene | null) => void;
  setActiveDay: (d: Day | null) => void;
  setSelectedPin: (id: string | null) => void;
  setActiveLocation: (id: string | null) => void;
  setDetailPin: (pin: Pin | null) => void;
  toggleTraffic: () => void;
  toggleMapMode: () => void;
  setMapMode: (v: boolean) => void;
  addAnnotation: (a: MapAnnotation) => void;
  setAnnotations: (a: MapAnnotation[]) => void;
  removeAnnotation: (id: string) => void;
  clearAnnotations: () => void;
  setActiveAnnotationTool: (t: AnnotationType | null) => void;
}

export const useMapStore = create<MapState>((set) => ({
  viewport: { lng: -118.2437, lat: 34.0522, zoom: 11 }, // Los Angeles default
  activeProject: null,
  activeScene: null,
  activeDay: null,
  selectedPinId: null,
  activeLocationId: null,
  detailPin: null,
  showTraffic: false,
  mapMode: false,
  annotations: [],
  activeAnnotationTool: null,

  setViewport: (viewport) => set({ viewport }),
  flyTo: (lng, lat, zoom = 15) => set({ viewport: { lng, lat, zoom } }),
  setActiveProject: (activeProject) => set({ activeProject, activeScene: null, activeDay: null }),
  setActiveScene: (activeScene) => set({ activeScene }),
  setActiveDay: (activeDay) => set({ activeDay }),
  setSelectedPin: (selectedPinId) => set({ selectedPinId }),
  // setActiveLocation clears detailPin so only one info panel shows at a time
  setActiveLocation: (activeLocationId) => set({ activeLocationId, detailPin: null }),
  // setDetailPin clears activeLocationId
  setDetailPin: (detailPin) => set({ detailPin, activeLocationId: null }),
  toggleTraffic: () => set((s) => ({ showTraffic: !s.showTraffic })),
  toggleMapMode: () => set((s) => ({ mapMode: !s.mapMode })),
  setMapMode: (mapMode) => set({ mapMode, ...(mapMode ? {} : { annotations: [], activeAnnotationTool: null }) }),
  addAnnotation: (a) => set((s) => ({ annotations: [...s.annotations, a] })),
  setAnnotations: (annotations) => set({ annotations }),
  removeAnnotation: (id) => set((s) => ({ annotations: s.annotations.filter(a => a.id !== id) })),
  clearAnnotations: () => set({ annotations: [] }),
  setActiveAnnotationTool: (activeAnnotationTool) => set({ activeAnnotationTool }),
}));
